import React, { useState, useEffect } from "react";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import { Button, Tooltip, Dropdown, Menu, Select, Spin } from "antd";
import { EditPencil, DeleteIcon, UniSvg, FilterButton } from "../../assets/icons";
import CreateManagerSidebar from "../../components/common/CreateManagerSidebar";
import DG from "../../atoms/DataTable/DataGrid";
import { Crown } from "lucide-react";
import ConfirmationModal from "../../atoms/ConfirmationModal";
import { toast } from "react-toastify";
import { DropDown } from "../../assets/icons";
import { filterIcon } from "../../assets/icons";
import { getOrganization, deleteManager } from "../../services/service";
import moment from 'moment';
import { useNavigate } from "react-router-dom";

const Organization = () => {
  const [isManagerSidebarVisible, setManagerSidebarVisible] = useState(false);
  const [loader, setLoader] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedManager, setSelectedManager] = useState(null);
  const [organizationData, setOrganizationData] = useState({
    name: "Organization Name",
    email: "org@example.com",
    subscription: "Trial",
    startDate: "2024-03-20",
  });

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteState, setDeleteState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [managers, setManagers] = useState([]);

  // Add pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: managers.length
  });

  // Add pagination handler
  const handlePageChange = (page, pageSize) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize
    });

    // Scroll to top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const navigate = useNavigate();

  const handleAddManager = () => {
    navigate('/add-worker', { state: { isManager: true } });
  };

  const handleEditManager = (manager) => {
    // Find complete manager data from organizationMembers array
    const completeManagerData = organizationMembers.find(
      user => user.id === manager.id
    );

    navigate('/add-worker', {
      state: {
        isManager: true,
        managerId: manager.id,
        managerData: completeManagerData // Pass the complete manager object with all fields
      }
    });
  };

  const handleDeleteManager = (row) => {
    // Check if trying to delete the main admin
    if (row.is_admin) {
      toast.error("⚠️ Cannot delete the main organization admin account. Deleting this account will delete the entire organization and all its data. Please contact support if you need to transfer ownership or delete the organization.", {
        autoClose: 6000,
        position: "top-center",
        style: {
          background: "#FEE2E2",
          color: "#991B1B",
          fontWeight: "600",
          fontSize: "14px",
          border: "2px solid #DC2626",
        }
      });
      return;
    }
    
    setDeleteState({ id: row.id, role: row.role });
    setDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      const targetId = deleteState.id;
      const roleKey = (deleteState.role || '').toLowerCase();
      const roleToDeleteFn = {
        manager: deleteManager,
      };
      const deleteFn = roleToDeleteFn[roleKey] || deleteManager;
      const response = await deleteFn(targetId);
      if (response?.success) {
        // Refresh organization data after successful deletion
        await fetchOrganizationData();
        toast.success("Member deleted successfully!");
      } else {
        toast.error(response?.message || "Error deleting member!");
      }
    } catch (error) {
      console.error('Error deleting member:', error);
      
      // Handle specific database constraint errors
      if (error?.response?.data?.message?.includes('foreign key constraint')) {
        toast.error("Cannot delete this member. They have associated records that must be removed first.");
      } else {
        toast.error(error?.response?.data?.message || "Error deleting member!");
      }
    } finally {
      setDeleteModal(false);
      setDeleteState(null);
      setIsLoading(false);
    }
  };

  const userDataString = localStorage.getItem('userData');

  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('manager');
  const [filterCount, setFilterCount] = useState(0);

  const getActiveFilterCount = () => {
    return filterCount;
  };

  const getManagerFilterMenu = () => (
    <Menu>
      <div className="p-4 -mt-2 -ml-3 w-[368px] rounded-[8px] bg-white filter-shadow">
        <div className="flex flex-col gap-3 font-medium text-[14px] text-[#4B5563]">
          <p>Filter By</p>
          <Select
            placeholder="Choose role"
            onChange={(value) => setSelectedFilter(value)}
            value={selectedFilter}
            className="w-full h-[44px] rounded-[4px] border border-[#E9E9E9] focus:outline-none -mt-1"
            suffixIcon={DropDown}
          >
            <Select.Option value="manager">Manager</Select.Option>
          </Select>

          <Button
            type="submit"
            onClick={() => {
              setFilterCount(0);
              setFilterVisible(false);
              const filtered = managers.filter(manager =>
                (manager.role || '').toLowerCase() === "manager"
              );
              setFilteredManagers(filtered);
            }}
            className="bg-[#F97316] border border-[#F97316] w-full h-[44px] font-medium text-[13px] text-white rounded-[4px]"
          >
            Apply
          </Button>
        </div>
      </div>
    </Menu>
  );

  const [filteredManagers, setFilteredManagers] = useState(managers);

  useEffect(() => {
    const filtered = managers.filter(manager =>
      (manager.username.toLowerCase().includes(searchText.toLowerCase()) ||
        manager.role.toLowerCase().includes(searchText.toLowerCase())) &&
      manager.role === "manager"
    );
    setFilteredManagers(filtered);
  }, [searchText, selectedFilter, managers]);

  const getPaginatedData = () => {
    const startIndex = (pagination.current - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredManagers.slice(startIndex, endIndex);
  };

  const [actionMenuVisible, setActionMenuVisible] = useState(false);
  const [organizationMembers, setOrganizationMembers] = useState([]);
  // Add useEffect for API call
  useEffect(() => {
    fetchOrganizationData();
    const userData = JSON.parse(userDataString);
    setActionMenuVisible(userData?.user?.user?.is_owner);
  }, []);
  const fetchOrganizationData = async () => {
    setLoader(true);
    try {
      const response = await getOrganization();
      if (response?.success) {
        setLoader(false);
        const orgData = response.data.organization;
        const ownerUser = (orgData?.OrganizationUsers || []).find((user) => user?.is_owner);
        const resolvedOrgEmail =
          orgData?.email ||
          orgData?.organization_email ||
          orgData?.contact_email ||
          ownerUser?.email ||
          orgData?.owner?.email ||
          "N/A";

        setOrganizationData({
          name: orgData.title || "N/A",
          email: resolvedOrgEmail,
          subscription: orgData?.OrganizationPlan?.title || "N/A",
          subscription_end_date: orgData.subscription_end_date || "N/A",
          plan_type: orgData.plan_type || "N/A",
        });

        // Sort by most recently created first and map organization users to managers
        const usersSortedByRecent = [...(orgData?.OrganizationUsers || [])].sort((a, b) => {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          return bTime - aTime;
        });
        const managerUsers = usersSortedByRecent.filter(
          (user) => (user?.role || "").toLowerCase() === "manager"
        );
        setOrganizationMembers(managerUsers);
        const mappedManagers = managerUsers.map(user => ({
          id: user.id,
          username: user.is_owner ? `${user.username} (Admin)` : user.username,
          email: user.email,
          role: user.role,
          joinedDate: moment(user.createdAt).format('YYYY-MM-DD'),
          assignProject: user.assignProject || 0,
          numOfProjects: user.numOfProjects || 0,
          status: true,
          is_admin: user.is_owner
        }));
        setManagers(mappedManagers);
        setFilteredManagers(mappedManagers);
      }
    } catch (error) {
      setLoader(false);
      console.error('Error fetching organization data:', error);
      toast.error('Failed to fetch organization data');
    }
  };

  return (
    <div className="w-full flex flex-col gap-5 items-center justify-center mb-20">
      <div className="col-12">
        <ListHeader
          mainHeading="Organization"
          btnShow={false}
          blinkBreadCrumText="Organization"
          searchShow={false}
        />
      </div>


      {
        loader ? <Spin /> :

          <>
            {/* Only show upgrade banner if not on Ultimate plan */}
            {!organizationData.subscription.toLowerCase().includes('ultimate') && (
              <div className="w-[98%] bg-[#1E3A8A] rounded-[8px] p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Crown className="text-[#F97316]" size={28} />
                    <div>
                      <h3 className="text-white font-semibold text-[20px] mb-2">Upgrade Your Plan</h3>
                      <p className="text-[14px] text-[#E5E7EB]">
                        Get unlimited managers, advanced analytics, and priority support
                      </p>
                    </div>
                  </div>
                  <button
                    className="w-[152px] h-[44px] bg-[#F97316] text-white font-medium text-[20px] rounded-[4px] hover:bg-orange-600"
                    onClick={() => window.open('https://buildtrack.ca/#subscription', '_blank', 'noopener,noreferrer')}
                  >
                    Upgrade Now
                  </button>
                </div>
              </div>
            )}

            <div className="w-[98%] rounded-[8px] bg-white p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-[20px] font-semibold text-[#1E3A8A] mb-2">Organization Details</h3>
                  <p className="text-[14px] text-[#4B5563]">Manage your organization and team members</p>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-4 mb-8">
                <div className="p-4 bg-[#F8F9FB] rounded-[8px] border border-[#E9E9E9]">
                  <p className="text-[14px] text-[#4B5563] mb-1">Organization Name</p>
                  <p className="text-[14px] font-medium text-[#1E3A8A]">{organizationData.name}</p>
                </div>
                <div className="p-4 bg-[#F8F9FB] rounded-[8px] border border-[#E9E9E9]">
                  <p className="text-[14px] text-[#4B5563] mb-1">Email</p>
                  <p className="text-[14px] font-medium text-[#1E3A8A]">{organizationData.email}</p>
                </div>
                <div className="p-4 bg-[#F8F9FB] rounded-[8px] border border-[#E9E9E9]">
                  <p className="text-[14px] text-[#4B5563] mb-1">Subscription</p>
                  <p className="text-[14px] font-medium text-[#1E3A8A]">{organizationData.subscription}</p>
                </div>

                <div className="p-4 bg-[#F8F9FB] rounded-[8px] border border-[#E9E9E9]">
                  <p className="text-[14px] text-[#4B5563] mb-1">End Date</p>
                  <p className="text-[14px] font-medium text-[#1E3A8A]">{organizationData.subscription_end_date}</p>
                </div>
              </div>

              {/* Members Section with Search and Filter */}
              <div>
                {/* First row: Heading and Filters */}
                <div className="w-full flex items-center justify-between">
                  <div className="font-semibold text-[20px] text-[#1E3A8A]">
                    Organization Members
                  </div>
                  <div className="col-lg-7 d-flex justify-content-end align-items-center list-header pr-0">
                    <div className="flex items-center">
                      <div className="search-input-field !h-[45px] d-flex align-items-center mr-2">
                        <i
                          className="fas fa-search pl-2"
                          style={{ color: "#6D7482", fontSize: "14.5px" }}
                        ></i>
                        <input
                          type="text"
                          placeholder={'Search managers'}
                          className="pl-2"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                      </div>

                      {/* Filter Icon */}
                      <div className="rounded w-[44px] h-[44px] bg-white flex items-center justify-center">
                        <Dropdown
                          overlay={getManagerFilterMenu()}
                          trigger={["click"]}
                          placement="bottomRight"
                          visible={filterVisible}
                          onVisibleChange={(visible) => setFilterVisible(visible)}
                          getPopupContainer={() => document.body}
                        >
                          <span className="cursor-pointer mt-[7px] relative">
                            {filterIcon}
                            {getActiveFilterCount() > 0 && (
                              <span className="absolute top-[13px] left-[52px] bg-[#F97316] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                                {getActiveFilterCount()}
                              </span>
                            )}
                          </span>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Second row: Add Manager Button */}
                <div className="flex justify-end px-2 mt-2 mb-4">
                  {actionMenuVisible && (
                    <button
                      onClick={handleAddManager}
                      className="w-[140px] py-1 rounded-[4px] bg-[#F97316] font-medium text-base text-white"
                    >
                      Add Manager
                    </button>
                  )}
                </div>

                {/* DataGrid */}
                <DG
                  index={11}
                  data={getPaginatedData()}
                  loader={false}
                  handleEdit={handleEditManager}
                  getData={handleDeleteManager}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  hideActions={actionMenuVisible}
                />
              </div>
            </div>
          </>
      }



      <ConfirmationModal
        show={deleteModal}
        handleClose={() => setDeleteModal(false)}
        handleConfirm={handleConfirmDelete}
        loading={isLoading}
        message="Do you really want to delete this member?"
      />
    </div>
  );
};

export default Organization;
